import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import axios from 'axios';
import CaptchaWidget from '../components/CaptchaWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookTourPage() {
  const [locations, setLocations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', location_id: '', preferred_date: '', preferred_time: '', message: '' });
  const [captcha, setCaptcha] = useState({ answer: '', token: '' });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/locations`).then(res => setLocations(res.data));
    axios.get(`${API}/tours/slots`).then(res => setSlots(res.data.slots));
    const params = new URLSearchParams(window.location.search);
    if (params.get('location')) setFormData(prev => ({...prev, location_id: params.get('location')}));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captcha.answer) { setError('Please solve the captcha.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/tours/book`, { ...formData, captcha_answer: captcha.answer, captcha_token: captcha.token });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="pt-20" data-testid="book-tour-page">
      <section className="py-24 md:py-32">
        <div className="max-w-[800px] mx-auto px-4 md:px-8">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-uf-red font-heading text-xl tracking-[0.3em] mb-2">VISIT US</p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white">Book a Gym Tour</h1>
            <p className="text-uf-textMuted text-lg mt-4">See our facilities and feel the difference.</p>
          </motion.div>

          {success ? (
            <motion.div className="bg-green-500/10 border border-green-500/50 p-12 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Check size={64} className="text-green-400 mx-auto mb-4" />
              <h2 className="font-heading text-3xl font-bold text-white mb-2">Tour Booked!</h2>
              <p className="text-uf-textMuted">We'll contact you shortly to confirm your appointment.</p>
              <Link to="/" className="btn-primary mt-6 inline-block"><span>Back to Home</span></Link>
            </motion.div>
          ) : (
            <motion.form onSubmit={handleSubmit} className="uf-form bg-uf-surface border border-uf-border p-8 md:p-12 space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="tour-form">
              {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 text-sm">{error}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="text" placeholder="Your Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required data-testid="tour-name" />
                <input type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="tour-email" />
              </div>
              <input type="tel" placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required data-testid="tour-phone" />
              <select value={formData.location_id} onChange={e => setFormData({...formData, location_id: e.target.value})} required data-testid="tour-location">
                <option value="">Select Location</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="date" value={formData.preferred_date} onChange={e => setFormData({...formData, preferred_date: e.target.value})} required min={new Date().toISOString().split('T')[0]} data-testid="tour-date" />
                <select value={formData.preferred_time} onChange={e => setFormData({...formData, preferred_time: e.target.value})} required data-testid="tour-time">
                  <option value="">Select Time</option>
                  {slots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
                </select>
              </div>
              <textarea placeholder="Any additional message (optional)" rows={3} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="resize-none" data-testid="tour-message" />
              <CaptchaWidget onVerify={(answer, token) => setCaptcha({ answer, token })} />
              <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="tour-submit"><span>{loading ? 'Booking...' : 'Book My Tour'}</span></button>
            </motion.form>
          )}
        </div>
      </section>
    </div>
  );
}
